import React from 'react';
import { observer, inject } from 'mobx-react';
import styles from './variantButton.scss';
import classNames from 'classnames';
import { VariantStore } from '../../../stores/VariantStore';
import Cross from '../../icons/cross';
import Diameter from '../../icons/diameter';
import Length from '../../icons/length';
import ArrowSmallIcon from '../../icons/arrowSmallIcon';
import { useViewportWidth } from '../../../helper/viewPortWidth';
import { VariantType } from '../../../stores/types';
import TabNavigationHelper from '../../../helper/tabNavigationHelper';
export interface IVariantButton {
    variantStore?: VariantStore;
    variantType: VariantType;
    isMobileHeader?: boolean;
    isStickyHeader?: boolean;
    isDeactive?: boolean;
}

export const VariantButton = inject('variantStore')(
    observer(({ variantStore, variantType, isMobileHeader, isStickyHeader, isDeactive }: IVariantButton) => {
        const { width } = typeof window !== 'undefined' ? useViewportWidth() : { width: 1024 };
        const enableContentServiceForPDP = variantStore?.mainStore?.enableContentServiceForPDP;
        const tabNavigation = TabNavigationHelper.instance;
        const colorButton = () => {
            const imgSrc =
                variantStore?.mainStore.cdnAssetsUrl +
                'colors64px/ColorIcon40/' +
                variantStore?.selectedColor.code.toString() +
                '.png';
            return (
                <>
                    <img
                        role='img' aria-label='color-icn'
                        data-testid='color-icn' data-semantic-id='color-icn'
                        className={styles.colorIcon}
                        src={imgSrc}
                        alt={variantStore?.mainStore.localization.generalL10n.colorIcon + ' ' +
                            variantStore?.selectedColor.name}
                        title={variantStore?.selectedColor.name}
                    />
                    {!isMobileHeader && (
                        <div role='button' aria-label='btn-title'
                            data-testid='btn-title' data-semantic-id='btn-title'
                            className={styles.button_label}>
                            {variantStore?.selectedColor.name}
                        </div>
                    )}
                </>
            );
        };

        const sizeButton = () => {
            return (
                <>
                    <div role='button' aria-label='value'
                        data-testid='selected_value' data-semantic-id='selected_value'
                        className={classNames(styles.selected_value, 'notranslate')}>
                        {variantStore?.selectedSize?.size.name}
                    </div>
                </>
            );
        };

        const specialButton = () => {
            const sizeTypeContent = () => {
                return (
                    <div className={styles.special}>
                        <Diameter />
                        <div className={styles.text}>
                            <div className={styles.value}>{variantStore?.variantSelectionCopy.selectedSizeType}</div>
                        </div>
                    </div>
                );
            };
            const valueContent = () => {
                return (
                    <div className={styles.special}>
                        <Length />
                        <div className={styles.text}>
                            <div className={styles.value}>{variantStore?.variantSelectionCopy.selectedSize?.name}</div>
                        </div>
                    </div>
                );
            };

            const valueClassNames = [styles.selected_value, styles.special_value].join(' ');
            return (
                <>
                    <div role='button' aria-label='btn-title' 
                        className={valueClassNames}
                        data-testid='selected_value' data-semantic-id='selected_value'>
                        {sizeTypeContent()}
                        {valueContent()}
                    </div>
                </>
            );
        };

        const modelButton = () => {
            const valueClassNames = [styles.selected_value, styles.model_value].join(' ');
            return (
                <div role='button' aria-label='btn-title'
                    className={valueClassNames}
                    data-testid='selected_value' data-semantic-id='selected_value'>
                    {variantStore?.selectedModel?.label}
                </div>
            );
        };

        const label = (labelText: string, captionText = '', insideButton = false) => {
            if (isStickyHeader) {
                return null;
            }
            const labelClassNames = classNames([styles.variant_title], {
                [styles.variantLabelInsideButton]: insideButton,
            });

            return (
                <div className={styles.variant_label_container}>
                    <div role='textbox' aria-label='var-title' 
                        data-testid='var-title' data-semantic-id='var-title'
                        className={labelClassNames}>
                        {labelText}
                    </div>
                    { captionText && <div className={styles.variant_caption}>{captionText}</div> }
                </div>
            );
        };

        const onClick = () => {
            if (variantStore?.isVariantPanelOpen && variantStore.variantType === variantType
                && !(variantStore?.mainStore.isMobileShop && isMobileHeader))
                variantStore.changeVariantPanelDisplay(false);
            else variantStore?.changeVariantPanelDisplay(true, variantType, width);
        };

        const content = () => {
            type VariantsWithContext = VariantType.Color | VariantType.Size | VariantType.Special | VariantType.Model;

            const calculateCaption = (variantType: VariantsWithContext): string => {
                const numberOfVariants = (() => {
                    switch(variantType) {
                        case VariantType.Color:
                            return variantStore?.mainStore.dataStore.variantSelection.availableColors.length;
                        case VariantType.Model:
                            return variantStore?.mainStore.dataStore.variantSelection.availableModels.length;
                        case VariantType.Size:
                        case VariantType.Special:
                            return variantStore?.mainStore.dataStore.variantSelection.availableSizeTypes.reduce(
                                (sum, sizeType) => sum + sizeType.sizes.length, 0
                            );
                        default:
                            return 0;
                    }
                })();

                if (
                    !numberOfVariants ||
                    numberOfVariants < 1 ||
                    (variantType === VariantType.Color && numberOfVariants === 1)
                ) {
                    return '';
                }

                const captionText = numberOfVariants === 1 ? 
                        variantStore?.mainStore.localization.generalL10n.variant
                        : variantStore?.mainStore.localization.generalL10n.variants;
                return `${numberOfVariants} ${captionText}`;
            }

            switch (variantType) {
                case VariantType.Color: {
                    const testId = 'variant-color-button';
                    const labelText = `${variantStore?.mainStore.localization.generalL10n.textColor}`;
                    const caption = calculateCaption(VariantType.Color);
                    return (
                        <>
                            {label(labelText, caption)}
                            {button(testId)}
                        </>
                    );
                }
                case VariantType.Size: {
                    const testId = 'variant-size-button';
                    const labelText = `${variantStore?.mainStore.localization.generalL10n.textSize}`;
                    const caption = calculateCaption(VariantType.Size);
                    return (
                        <>
                            {label(labelText, caption)}
                            {button(testId)}
                        </>
                    );
                }
                case VariantType.Special: {
                    const specialVariantTitle = `${variantStore?.mainStore.localization.generalL10n.textSize}`;
                    const testId = 'variant-special-button';
                    const caption = calculateCaption(VariantType.Special);
                    return (
                        <>
                            {label(specialVariantTitle, caption)}
                            {button(testId)}
                        </>
                    );
                }
                case VariantType.Model: {
                    const caption = calculateCaption(VariantType.Model);
                    const testId = 'variant-model-button';
                    const labelText = `${variantStore?.mainStore.localization.generalL10n.textModel}`;
                    return (
                        <>
                            {label(labelText, caption)}
                            {button(testId)}
                        </>
                    );
                }
                default:
            }
        };

        const button = (testId: string) => {
            return (
                <div
                    role='button'
                    data-testid={testId}
                    data-semantic-id={testId}
                    className={classNames(styles.button, {
                        [styles.variant_special_button]: variantType === VariantType.Special,
                        [styles.isContentService]: enableContentServiceForPDP,
                    })}
                    onClick={onClick}
                    ref={(el)=> tabNavigation.setFocusAtt(el)}
                >
                    {buttonContent()}
                    {buttonIcon()}
                </div>
            );
        };

        const buttonContent = () => {
            switch (variantType) {
                case VariantType.Color: {
                    return <>{colorButton()}</>;
                }
                case VariantType.Size: {
                    return <>{sizeButton()}</>;
                }
                case VariantType.Special: {
                    return <>{specialButton()}</>;
                }
                case VariantType.Model: {
                    return <>{modelButton()}</>;
                }
                default:
            }
        };

        const buttonIcon = () => {
            const showCross = variantStore?.variantType === variantType && variantStore.isVariantPanelOpen;
            if (!showCross && isDeactive) {
                return <></>;
            }

            const selectText = variantStore?.mainStore.localization.generalL10n.select;

            // isContentService

            if (showCross) {
                return <Cross />;
            } else if (isMobileHeader) {
                return <ArrowSmallIcon />
            } else {
                return (
                    <div className={classNames(styles.select_bubble, {
                            [styles.isContentService]: enableContentServiceForPDP,
                        })}
                    >
                        <span className={styles.select_bubble_text}>{ selectText }</span>
                        <ArrowSmallIcon />
                    </div>
                );
            }
        };

        const containerClassNames = (isStickyHeader
            ? [styles.variant_button, styles.isStickyHeader]
            : [styles.variant_button]
        ).join(' ');

        const isOnlyVariantButton = {
            [styles.only_variant_button]: isStickyHeader && variantStore?.variantButtonCount === 1,
        };

        const isColorVariantButton = { [styles.color_variant_button]: variantType === VariantType.Color };

        return (
            <div
                data-testid='variant-button' data-semantic-id='variant-button'
                className={classNames(containerClassNames, isOnlyVariantButton, isColorVariantButton, {
                    [styles.deactive]: isDeactive,
                })}
            >
                {content()}
            </div>
        );
    })
);

